// S2-CC20.6b.4: CallLogView — composition that wraps useLLMCalls() with
// the filter chip bar + table + Load-more footer + empty/error states.
//
// State owned by this view:
//   - statusFilter: string[]  (UI chip values: 'Success' | 'Blocked' | 'Errors')
//   - providerFilter: string[]  (UI chip values: 'Anthropic' | 'OpenAI')
// We translate these UI rollups to backend ENUM arrays before passing to
// useLLMCalls. An empty array = "All" = no backend filter (the hook omits
// the param from the query string).
//
// Wireframe: docs/sprint-2/wireframes/s2-cc6-llm-control-center.html
// (Section 6 — Call Log). This view does NOT render the decision-audit
// drawer yet (deferred follow-up); row clicks are no-op today.
import { useState, useMemo } from 'react';
import { Card } from '../Card';
import { EmptyState } from '../EmptyState';
import { StatusPill } from '../StatusPill';
import { FilterChip, FilterChipGroup } from '../FilterChip';
import { useLLMCalls } from '../../hooks/useLLMCalls';
import type { LlmCallRow, LlmCallStatus } from '../../types/llm';
import './CallLogView.css';

// UI chip label → backend ENUMs. "All" is handled implicitly (empty array).
// The 4 UI groups collapse the 8 ENUMs into operator-friendly buckets per
// the wireframe's filter-bar design note (lines 248-254 of the HTML).
const STATUS_GROUPS: Record<string, LlmCallStatus[]> = {
  Success: ['success'],
  Blocked: ['budget_blocked', 'policy_blocked', 'gateway_paused'],
  Errors: ['auth_failed', 'provider_error', 'rate_limited', 'timeout'],
};

const PROVIDER_GROUPS: Record<string, string[]> = {
  Anthropic: ['anthropic'],
  OpenAI: ['openai'],
};

// Human-readable chip labels, in wireframe order.
const STATUS_CHIPS = ['Success', 'Blocked', 'Errors'] as const;
const PROVIDER_CHIPS = ['Anthropic', 'OpenAI'] as const;

// Format an ISO timestamp as HH:MM:SS local wall-clock. Used for the Time
// column — the full timestamp is available on hover via a title attr.
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

// Cost color class based on magnitude. Mirrors the wireframe thresholds:
// < 1¢  → muted (low/noise)
// 1-10¢ → primary text (normal)
// > 10¢ → warning (high — investigate)
function costClass(cents: number): 'low' | 'normal' | 'high' {
  if (cents < 1) return 'low';
  if (cents <= 10) return 'normal';
  return 'high';
}

// Format cents to match the wireframe: integer → "0¢", fractional → "2.9¢".
function formatCost(cents: number): string {
  if (cents === 0) return '0¢';
  if (Number.isInteger(cents)) return `${cents}¢`;
  return `${cents.toFixed(2).replace(/\.?0+$/, '')}¢`;
}

// Format tokens as "in → out". Returns em-dashes when the row blocked
// before tokens were counted (blocked statuses have 0 on both sides).
function formatTokens(row: LlmCallRow): string {
  const isBlockedWithNoTokens =
    row.input_tokens === 0 &&
    row.output_tokens === 0 &&
    row.status !== 'success';
  if (isBlockedWithNoTokens) return '— → —';
  return `${row.input_tokens.toLocaleString()} → ${row.output_tokens.toLocaleString()}`;
}

export function CallLogView() {
  // Controlled chip state. Empty array = "All" selected.
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [providerFilter, setProviderFilter] = useState<string[]>([]);

  // Translate UI chip arrays → backend ENUM arrays.
  // useMemo so the array identity is stable unless filters change, keeping
  // the hook's internal effect from refetching unnecessarily.
  const hookParams = useMemo(() => {
    const statusEnums = statusFilter.flatMap(label => STATUS_GROUPS[label] ?? []);
    const providerEnums = providerFilter.flatMap(label => PROVIDER_GROUPS[label] ?? []);
    return { status: statusEnums, provider: providerEnums, limit: 50 };
  }, [statusFilter, providerFilter]);

  const { calls, loading, error, hasMore, loadMore, refresh } = useLLMCalls(hookParams);

  // Toggle chip membership in a filter array.
  const toggleFilter = (
    arr: string[],
    setter: (v: string[]) => void,
    label: string,
  ) => {
    if (arr.includes(label)) {
      setter(arr.filter(x => x !== label));
    } else {
      setter([...arr, label]);
    }
  };

  // "All" click clears the filter array entirely.
  const clearFilter = (setter: (v: string[]) => void) => setter([]);

  const initialLoading = loading && calls.length === 0 && !error;

  return (
    <Card variant="standard" padding="default" className="airgent-llm-call-log-view">
      <div className="airgent-llm-card-header">
        <div>
          <div className="airgent-llm-card-title">Call Log</div>
          <div className="airgent-llm-card-sub">
            Durable ledger. Latest first.
          </div>
        </div>
        <div className="airgent-llm-card-sub">
          {calls.length > 0 ? `${calls.length} rows shown` : ''}
        </div>
      </div>

      {/* Filter bar — status + provider groups, chip with "All" toggle. */}
      <div className="airgent-llm-chip-bar">
        <FilterChipGroup label="Status">
          <FilterChip
            label="All"
            selected={statusFilter.length === 0}
            onToggle={() => clearFilter(setStatusFilter)}
          />
          {STATUS_CHIPS.map(label => (
            <FilterChip
              key={label}
              label={label}
              selected={statusFilter.includes(label)}
              onToggle={() => toggleFilter(statusFilter, setStatusFilter, label)}
            />
          ))}
        </FilterChipGroup>

        <FilterChipGroup label="Provider">
          <FilterChip
            label="All"
            selected={providerFilter.length === 0}
            onToggle={() => clearFilter(setProviderFilter)}
          />
          {PROVIDER_CHIPS.map(label => (
            <FilterChip
              key={label}
              label={label}
              selected={providerFilter.includes(label)}
              onToggle={() => toggleFilter(providerFilter, setProviderFilter, label)}
            />
          ))}
        </FilterChipGroup>
      </div>

      {/* Error banner — appears above content so it's visible even if we
          have a previous page's rows cached. */}
      {error && (
        <div className="airgent-llm-error-banner" role="alert">
          <span>Failed to load calls: {error}</span>
          <button
            type="button"
            className="airgent-llm-retry-btn"
            onClick={refresh}
          >
            Retry
          </button>
        </div>
      )}

      {/* Body — loading / empty / table. */}
      {initialLoading ? (
        <div className="airgent-llm-loading-caption">Loading calls…</div>
      ) : calls.length === 0 && !error ? (
        <EmptyState
          title="No calls yet"
          detail="Fire a probe: POST /v1/agent/probe"
          variant="solid"
        />
      ) : (
        <>
          <table className="airgent-llm-calls-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Provider</th>
                <th>Model</th>
                <th className="airgent-hide-mobile">Role / Agent</th>
                <th>Status</th>
                <th className="airgent-hide-mobile">Tokens</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {calls.map(row => (
                <tr key={row.id}>
                  <td className="airgent-llm-mono" title={row.timestamp}>
                    {formatTime(row.timestamp)}
                  </td>
                  <td>
                    <span className="airgent-llm-provider-badge">{row.provider}</span>
                  </td>
                  <td className="airgent-llm-mono">{row.model}</td>
                  <td className="airgent-llm-mono airgent-hide-mobile">
                    {row.role_instance_id ?? '—'}
                    {row.agent_instance_id && (
                      <>
                        <br />
                        <span className="airgent-llm-row-sub">
                          {row.agent_instance_id}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <StatusPill status={row.status} size="sm" />
                  </td>
                  <td className="airgent-llm-mono airgent-hide-mobile">
                    {formatTokens(row)}
                  </td>
                  <td className={`airgent-llm-cost airgent-llm-cost-${costClass(row.cost_cents)}`}>
                    {formatCost(row.cost_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="airgent-llm-calls-footer">
            <span className="airgent-llm-page-caption">
              Showing {calls.length} rows · cursor=
              {calls.length > 0 ? String(calls[calls.length - 1].id) : '—'}
            </span>
            {hasMore ? (
              <button
                type="button"
                className="airgent-llm-load-more-btn"
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Load 50 more'}
              </button>
            ) : (
              <span className="airgent-llm-page-caption">
                End of log · {calls.length} rows shown
              </span>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
